import React from 'react';
import { motion } from 'framer-motion';
import {
  FiArrowRight,
  FiBarChart2,
  FiFacebook,
  FiFileText,
  FiGlobe,
  FiLayout,
  FiMousePointer,
  FiSearch,
  FiTarget,
  FiTrendingUp,
  FiVideo,
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const platforms = [
  {
    icon: FiSearch,
    title: 'Search Lead Generation',
    desc: 'Capture high-intent prospects when they are actively searching for your products or services.',
  },
  {
    icon: FiFacebook,
    title: 'Social Lead Generation',
    desc: 'Reach targeted audiences across Facebook, Instagram, and other social platforms with campaigns built to generate enquiries.',
  },
  {
    icon: FiVideo,
    title: 'Short-Form Lead Campaigns',
    desc: 'Create thumb-stopping campaigns that connect with modern audiences and turn attention into qualified leads.',
  },
  {
    icon: FiGlobe,
    title: 'Retargeting & Follow-Up',
    desc: 'Stay visible to interested visitors and guide them back toward a conversation, enquiry, or purchase.',
  },
  {
    icon: FiTarget,
    title: 'Audience Strategy',
    desc: 'Build audience segments based on intent, behaviour, interests, location, and customer value.',
  },
  {
    icon: FiTrendingUp,
    title: 'Lead Quality Optimisation',
    desc: 'Improve budgets, creatives, landing pages, and targeting using real performance and conversion data.',
  },
];

const GoogleAds = () => {
  return (
    <section
      id="ads-strategy"
      className="relative overflow-hidden bg-slate-950 py-24 text-white"
    >
      <div className="pointer-events-none absolute -ml-64 -mt-64 h-[500px] w-[500px] rounded-full bg-emerald-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute -mb-64 -mr-64 bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-blue-500/10 blur-[120px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-20 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl"
          >
            <h2 className="mb-4 flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em] text-emerald-400">
              <SafeIcon icon={FiBarChart2} />
              Lead Generation Strategy
            </h2>

            <p className="mb-6 text-4xl font-extrabold md:text-5xl">
              Turn Attention Into Consistent Qualified Leads
            </p>

            <p className="text-xl font-medium leading-relaxed text-slate-400">
              We create connected lead generation systems across search, social media,
              content, landing pages, and follow-up channels. Paid ads can be part of
              the strategy, but every activity is focused on attracting the right
              prospects and converting them into real business opportunities.
            </p>
          </motion.div>

          <motion.a
            href="https://go.digitalgigs.eu/chat"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center self-start rounded-2xl bg-emerald-500 px-8 py-4 font-black text-slate-950 shadow-xl shadow-emerald-500/20 transition-all hover:bg-emerald-400 lg:self-end"
          >
            Build My Lead Strategy
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </motion.a>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {platforms.map((platform, index) => (
            <motion.div
              key={platform.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="group rounded-[2.5rem] border border-white/10 bg-white/5 p-8 transition-all hover:border-emerald-400/50 hover:bg-white/[0.08]"
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/20 text-emerald-400 transition-all group-hover:bg-emerald-500 group-hover:text-slate-950">
                <SafeIcon icon={platform.icon} className="text-2xl" />
              </div>

              <h3 className="mb-4 text-2xl font-bold">{platform.title}</h3>
              <p className="font-medium leading-relaxed text-slate-400">
                {platform.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 grid gap-8 rounded-[3rem] border border-white/10 bg-gradient-to-br from-slate-900 to-slate-800 p-8 md:grid-cols-3 md:p-12">
          <div className="flex items-start gap-5">
            <SafeIcon icon={FiMousePointer} className="mt-1 text-2xl text-emerald-400" />
            <div>
              <p className="text-xl font-bold">Right Audience</p>
              <p className="mt-2 text-slate-400">
                Reach people who are most likely to need and value your offer.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-5">
            <SafeIcon icon={FiLayout} className="mt-1 text-2xl text-blue-400" />
            <div>
              <p className="text-xl font-bold">Right Conversion Path</p>
              <p className="mt-2 text-slate-400">
                Use landing pages, forms, WhatsApp, and follow-up systems to turn
                interest into action.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-5">
            <SafeIcon icon={FiFileText} className="mt-1 text-2xl text-emerald-400" />
            <div>
              <p className="text-xl font-bold">Clear Reporting</p>
              <p className="mt-2 text-slate-400">
                Track enquiries, lead quality, sales, and return on your marketing
                investment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleAds;
